/**
 * Pure stock math shared by the API and the SPA.
 *
 * The database is the authority (see `bizy_reserve_order_stock`); these helpers
 * exist so the POS can disable checkout before a round-trip, and unit tests can
 * describe the rules without a Postgres instance.
 */

export interface StockComponentRef {
  product_id: string;
  variant_id?: string | null;
  quantity: number;
}

export interface BundleComponentSnapshot {
  product_id: string;
  variant_id: string | null;
  name: string;
  quantity: number;
}

export interface StockDemand {
  product_id: string;
  variant_id: string | null;
  quantity: number;
}

export interface StockShortage extends StockDemand {
  available: number;
}

export interface ReservationLineInput {
  product_id: string;
  variant_id?: string | null;
  quantity: number;
  /** Present for bundle products: one unit of the bundle consumes these. */
  components?: readonly StockComponentRef[] | null;
}

export const stockKey = (productId: string, variantId?: string | null): string =>
  variantId ? `${productId}:${variantId}` : productId;

/**
 * How many bundles can be built from the components on hand. `stock` maps a
 * `stockKey` to the available units; missing keys count as zero.
 */
export const deriveBundleAvailability = (
  components: readonly StockComponentRef[],
  stock: Readonly<Record<string, number>>,
): number => {
  if (components.length === 0) return 0;

  let units = Number.POSITIVE_INFINITY;
  for (const component of components) {
    const perBundle = Math.max(1, Math.trunc(component.quantity));
    const available = stock[stockKey(component.product_id, component.variant_id)] ?? 0;
    units = Math.min(units, Math.floor(available / perBundle));
  }

  return Number.isFinite(units) ? Math.max(0, units) : 0;
};

/** Flattens cart lines into the per-product demand a checkout would reserve. */
export const expandReservationDemand = (
  lines: readonly ReservationLineInput[],
): StockDemand[] => {
  const demand = new Map<string, StockDemand>();

  const add = (productId: string, variantId: string | null, quantity: number) => {
    if (quantity <= 0) return;
    const key = stockKey(productId, variantId);
    const current = demand.get(key);
    demand.set(key, {
      product_id: productId,
      variant_id: variantId,
      quantity: (current?.quantity ?? 0) + quantity,
    });
  };

  for (const line of lines) {
    const components = line.components ?? [];
    if (components.length > 0) {
      for (const component of components) {
        add(component.product_id, component.variant_id ?? null, component.quantity * line.quantity);
      }
      continue;
    }
    add(line.product_id, line.variant_id ?? null, line.quantity);
  }

  return [...demand.values()];
};

/** Which of those demands the current balances cannot satisfy. */
export const planReservation = (
  lines: readonly ReservationLineInput[],
  stock: Readonly<Record<string, number>>,
): { ok: boolean; demand: StockDemand[]; shortages: StockShortage[] } => {
  const demand = expandReservationDemand(lines);
  const shortages = demand
    .map((entry) => ({
      ...entry,
      available: stock[stockKey(entry.product_id, entry.variant_id)] ?? 0,
    }))
    .filter((entry) => entry.available < entry.quantity);

  return { ok: shortages.length === 0, demand, shortages };
};

export interface ReturnableLedger {
  /** Units reserved when the order was created. */
  reserved_units: number;
  /** Units already returned undamaged. */
  released_units: number;
  /** Units already returned damaged and written off. */
  written_off_units: number;
}

/** Units of one order line that can still be returned (good or damaged). */
export const returnableUnits = (ledger: ReturnableLedger): number =>
  Math.max(
    0,
    Math.trunc(ledger.reserved_units) -
      Math.trunc(ledger.released_units) -
      Math.trunc(ledger.written_off_units),
  );

export interface ReturnLineRequest {
  order_item_id: string;
  quantity: number;
  damaged_quantity: number;
}

/** Rejects negative or over-return requests before they reach the database. */
export const validateReturnLines = (
  lines: readonly ReturnLineRequest[],
  outstandingByItem: Readonly<Record<string, number>>,
): { ok: boolean; errors: string[] } => {
  const errors: string[] = [];

  for (const line of lines) {
    const units = Math.trunc(line.quantity);
    const damaged = Math.trunc(line.damaged_quantity);

    if (units < 0 || damaged < 0) {
      errors.push(`${line.order_item_id}: quantities cannot be negative`);
      continue;
    }
    if (units + damaged === 0) continue;

    const outstanding = outstandingByItem[line.order_item_id] ?? 0;
    if (units + damaged > outstanding) {
      errors.push(
        `${line.order_item_id}: ${units + damaged} requested but only ${outstanding} outstanding`,
      );
    }
  }

  return { ok: errors.length === 0, errors };
};

/** True when every rental line of an order has come back (good or damaged). */
export const isFullyReturned = (ledgers: readonly ReturnableLedger[]): boolean =>
  ledgers.every((ledger) => returnableUnits(ledger) === 0);
